import { UserId } from 'api/models';
import { BUILD_ID } from 'config/environment';
import { logger } from 'services';
import { isHippaAccount } from 'utils/is-hippa-account';
import { isLocalhost } from 'utils/is-localhost';
import {
  isOrbitsEnvironment,
  isShardTestEnvironment,
} from 'utils/is-nitro-env';

interface LogRocketUserTraits {
  [propName: string]: string | number | boolean;
}

export interface UserTraits extends LogRocketUserTraits {
  name: string;
  email: string;
  accountId: number;
  emailId: string;
  fullName: string;
  userId: number;
}

function shouldSkipLogRocket(): boolean {
  if (
    isLocalhost() ||
    window.location.pathname.includes('/login') ||
    isOrbitsEnvironment() ||
    isShardTestEnvironment()
  ) {
    logger.log(
      'LogRocket is disabled on localhost, login page, or restricted env',
    );

    return true;
  }

  if (isHippaAccount()) {
    logger.log('LogRocket is disabled for hippa compliant accounts');

    return true;
  }

  return false;
}

/**
 * Loads LogRocket + logrocket-react in a separate chunk (not on the main boot waterfall),
 * then initializes and identifies the user. Safe to fire-and-forget.
 */
export async function startLogRocketSession(
  traits: UserTraits,
  id?: UserId,
): Promise<void> {
  if (shouldSkipLogRocket()) {
    return;
  }

  const [{ default: LogRocket }, { default: setupLogRocketReact }] =
    await Promise.all([
      import(/* webpackChunkName: "logrocket" */ 'logrocket'),
      import(/* webpackChunkName: "logrocket" */ 'logrocket-react'),
    ]);

  setupLogRocketReact(LogRocket);

  LogRocket.init('oieheh/rocketlane-prod', {
    release: BUILD_ID,
    network: {
      requestSanitizer: (request) => {
        if (request.url.toLowerCase().indexOf('authn') !== -1) {
          request.body = undefined;
        }
        request.headers['api-key'] = undefined;

        return request;
      },
    },
  });

  if (id == null) {
    LogRocket.identify(traits);
  } else {
    LogRocket.identify(id[1].toString(), traits);
  }

  if (!isShardTestEnvironment()) {
    logger.debug('identifying user', { id, traits });
  }
}
